// src/cockpit4/components/SecurityHardening.tsx
// ============================================
// 安全加固（设置中心 P2-功能8）- v2.0 详细改进计划
// SSO 单点登录 / 2FA 双因素认证 / 数据加密 / 安全扫描 / 登录异常检测 / 审计报告
// 数据来源：localStorage（xeaos_security_hardening）
// ============================================

import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, KeyRound, Fingerprint, Lock, ScanSearch, AlertTriangle } from "lucide-react";

interface SecurityItem {
  name: string;
  status: boolean;
  desc: string;
}

const INITIAL_ITEMS: SecurityItem[] = [
  { name: "SSO 单点登录", status: true, desc: "支持 OAuth2 / SAML，对接企业微信/钉钉" },
  { name: "双因素认证（2FA）", status: true, desc: "TOTP 动态口令 + 短信验证码" },
  { name: "传输加密", status: true, desc: "全站 HTTPS / TLS 1.3" },
  { name: "存储加密", status: true, desc: "敏感字段 AES-256 加密存储" },
  { name: "登录异常检测", status: true, desc: "异地登录 / 频繁尝试自动告警" },
  { name: "安全扫描", status: false, desc: "每周自动依赖漏洞扫描（暂未启用）" },
];

const KEY = "xeaos_security_hardening";

function load(): SecurityItem[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw) as SecurityItem[];
  } catch { /* ignore */ }
  return INITIAL_ITEMS;
}

export default function SecurityHardening() {
  const [items, setItems] = useState<SecurityItem[]>(load);

  const stats = useMemo(() => {
    const enabled = items.filter((i) => i.status).length;
    const score = Math.round((enabled / items.length) * 100);
    return { enabled, score };
  }, [items]);

  const toggle = (idx: number) => {
    setItems((prev) => {
      const next = prev.map((i, k) => (k === idx ? { ...i, status: !i.status } : i));
      try { localStorage.setItem(KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <ShieldCheck size={14} className="text-emerald-400" />
        <h3 className="text-[12px] font-medium text-white">安全加固</h3>
        <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">P2-功能8 · Security</span>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <MetricCard label="安全评分" value={`${stats.score}/100`} color={stats.score >= 80 ? "#34D399" : "#FBBF24"} />
        <MetricCard label="已启用项" value={`${stats.enabled}/${items.length}`} color="#38BDF8" />
        <MetricCard label="安全等级" value="高" color="#A78BFA" />
      </div>

      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="flex items-center gap-2 mb-2">
          <KeyRound size={11} className="text-emerald-400" />
          <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">安全能力清单</span>
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          {items.map((it, idx) => (
            <div key={it.name} className="flex items-center gap-2 rounded-md border border-white/[0.04] bg-white/[0.01] px-2.5 py-2">
              <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded ${it.status ? "bg-emerald-500/15 text-emerald-400" : "bg-gray-500/15 text-gray-500"}`}>{it.status ? "已启用" : "未启用"}</span>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] text-white">{it.name}</div>
                <div className="text-[8px] font-mono text-gray-600">{it.desc}</div>
              </div>
              <button onClick={() => toggle(idx)} className="text-[8px] font-mono text-gray-400 hover:text-white">{it.status ? "停用" : "启用"}</button>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 text-[9px] font-mono">
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5">
          <div className="flex items-center gap-1.5 text-amber-300 mb-1"><ScanSearch size={10} /> 最近安全事件</div>
          <div className="text-gray-400">近 30 天：0 次越权访问 · 2 次登录异常已拦截 · 1 次高危端口扫描已封禁</div>
        </div>
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5">
          <div className="flex items-center gap-1.5 text-red-300 mb-1"><AlertTriangle size={10} /> 建议</div>
          <div className="text-gray-400">建议启用「安全扫描」并配置每周漏洞报告推送，评分可提升至 100</div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2">
      <div className="text-[9px] font-mono text-gray-500 uppercase">{label}</div>
      <div className="text-sm font-semibold font-mono mt-0.5" style={{ color }}>{value}</div>
    </motion.div>
  );
}
